// A failed scene write the user made is told once per gesture or burst, and the rows of every failed one are read again and shown as the server has them.

import { toast } from 'sonner';
import type { AnyKind, FailedWrite } from './sceneWrites';

/** Failures this close together are one burst, told in one toast. */
export const FAILURE_BURST_MS = 400;

/** How long the editor's own handling of a failure (a rollback's toast, say) has to tell the user first. */
const OWN_TELLING_MS = 100;

/** How long the toast waits for the rows read again; told sooner, it does not say the editor shows what was saved. */
const SHOWN_WITHIN_MS = 2000;

/** How long after a failed read of a failed write's rows they are read again, doubled for each failure in a row. */
export const READ_AGAIN_MS = 2000;
/** The longest it waits. */
const MAX_READ_AGAIN_MS = 60000;

const NOUNS: Record<AnyKind, [one: string, many: string]> = {
  asset: ['asset', 'assets'],
  keyframe: ['keyframe', 'keyframes'],
  clip: ['clip', 'clips'],
  uiElement: ['screen message', 'screen messages'],
  material: ['material', 'materials'],
  scene: ['scene settings', 'scene settings'],
  story: ['story', 'stories'],
  assetType: ['library item', 'library items'],
  uiElementType: ['screen message style', 'screen message styles'],
  materialType: ['material preset', 'material presets'],
  vfx: ['effect', 'effects'],
};

const opOf = ({ write }: FailedWrite) => (write.op === 'create' || write.op === 'delete' ? write.op : 'change');

/** Whether a write failed for a reason that passes on its own: no answer, the rate limit, or the server. */
const passing = ({ status }: FailedWrite) => status === 0 || status === 429 || status >= 500;

/** What the user is told of failed writes, in plain English: what did not save, or may not have, and, once their rows read again are `shown`, that the editor shows what was saved. */
export function failureMessage(failures: readonly FailedWrite[], shown: boolean): string {
  const retry = failures.some(passing) ? ' Check your connection and try again.' : '';
  const { kind } = failures[0].write;
  const op = opOf(failures[0]);
  // A write that got no answer may have saved: its rows, read again, show whether it did
  const unsure = failures.some(failure => failure.status === 0);
  const showing = shown ? ' The editor now shows what was saved.' : '';
  const so = (what: string) => (shown ? `, so ${what}` : '');
  if (failures.some(failure => failure.write.kind !== kind || opOf(failure) !== op)) {
    return (unsure ? `Some of your changes may not have saved.${showing}` : `Couldn't save some of your changes${so('they were put back')}.`) + retry;
  }
  // Rows of the kind written; a write that names none of its own (a material upsert) counts as one
  const named = failures.flatMap((failure, i) => {
    const own = failure.rows.filter(row => row.startsWith(`${kind}:`));
    return own.length > 0 ? own : [`#${i}`];
  });
  const count = new Set(named).size;
  const [one, many] = NOUNS[kind];
  if (unsure) {
    if (op === 'create') return (count === 1 ? `That ${one} may not have been added.` : `${count} ${many} may not have been added.`) + showing + retry;
    if (op === 'delete') return (count === 1 ? `That ${one} may not have been deleted.` : `${count} ${many} may not have been deleted.`) + showing + retry;
    return (count === 1 ? `That ${one} change may not have saved.` : `Changes to ${count} ${many} may not have saved.`) + showing + retry;
  }
  if (op === 'create') return (count === 1 ? `Couldn't add that ${one}${so('it was taken out')}.` : `Couldn't add ${count} ${many}${so('they were taken out')}.`) + retry;
  if (op === 'delete') return (count === 1 ? `Couldn't delete that ${one}${so('it was put back')}.` : `Couldn't delete ${count} ${many}${so('they were put back')}.`) + retry;
  return (count === 1 ? `Couldn't save that ${one} change${so('it was put back')}.` : `Couldn't save changes to ${count} ${many}${so('they were put back')}.`) + retry;
}

export interface FailedWriteDeps {
  /** Resolves once these gestures have closed (recorded or rolled back) and no undo or redo is waiting or applying. */
  settled(gestureIds: string[]): Promise<void>;
  /** Reads a failed write's rows again and shows them to the renderer and the editor as the server has them. */
  reread(failure: FailedWrite): Promise<void>;
  /** Whether this scene is still open, so a failed write's rows in it are still worth reading again. */
  open(sceneId: string): boolean;
}

/** Takes each failed write: a burst is told once its gestures close, unless the editor told it already, and each row is read again once idle, until a read of it works. */
export function failedWrites({ settled, reread, open }: FailedWriteDeps): (failure: FailedWrite) => void {
  const ours = new Set<string | number>();
  let burst: { failures: FailedWrite[]; toastsBefore: number } | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;

  // Whether another part of the editor has shown an error since the burst began
  const toldSince = (index: number) => toast.getHistory().slice(index)
    .some(shown => 'type' in shown && (shown.type === 'error' || shown.type === 'warning') && !ours.has(shown.id));

  const readAgain = async (failure: FailedWrite) => {
    await failure.settled();
    await reread(failure);
  };

  // A read that failed (offline, say) is made again, waiting longer each time, while the scene is open
  async function readAgainLater(failure: FailedWrite): Promise<void> {
    for (let failed = 0; ; failed += 1) {
      await new Promise(resolve => setTimeout(resolve, Math.min(READ_AGAIN_MS * 2 ** failed, MAX_READ_AGAIN_MS)));
      if (!open(failure.sceneId)) return;
      try {
        return await readAgain(failure);
      } catch {
        // Made again after a longer wait; the first failure was logged
      }
    }
  }

  // Whether the first read of a failed write's rows showed them
  const show = (failure: FailedWrite): Promise<boolean> => readAgain(failure).then(() => true, error => {
    console.warn('[undo] could not read the rows of a failed write again', error);
    void readAgainLater(failure);
    return false;
  });

  async function close({ failures, toastsBefore }: { failures: FailedWrite[]; toastsBefore: number }): Promise<void> {
    await settled([...new Set(failures.flatMap(failure => (failure.gestureId ? [failure.gestureId] : [])))])
      .catch(error => console.warn('[undo] could not wait for the gestures of failed writes', error));
    await new Promise(resolve => setTimeout(resolve, OWN_TELLING_MS));
    const toldAlready = toldSince(toastsBefore);
    // The same write failing twice in the burst is read once; a material's names its asset, but its read is of that material
    const byWrite = new Map<string, FailedWrite>();
    for (const failure of failures) {
      const { write } = failure;
      const key = [write.kind, write.op, 'id' in write ? write.id : '', ...failure.rows].join(',');
      if (!byWrite.has(key)) byWrite.set(key, failure);
    }
    const read = Promise.all([...byWrite.values()].map(show)).then(shown => shown.every(Boolean));
    let wait: ReturnType<typeof setTimeout> | undefined;
    const shown = await Promise.race([read, new Promise<boolean>(resolve => { wait = setTimeout(() => resolve(false), SHOWN_WITHIN_MS); })]);
    clearTimeout(wait);
    // Only the user's edits are told: a write the editor or the renderer made on its own, or an undo's, is only read again
    const told = failures.filter(failure => failure.byUser && failure.status !== 404);
    if (told.length > 0 && !toldAlready) ours.add(toast.error(failureMessage(told, shown)));
  }

  return failure => {
    const current = burst ?? (burst = { failures: [], toastsBefore: toast.getHistory().length });
    current.failures.push(failure);
    clearTimeout(timer);
    timer = setTimeout(() => {
      burst = null;
      void close(current);
    }, FAILURE_BURST_MS);
  };
}
